import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "../lib/firebase.js";

/* Subscribe to a Firestore collection (or subcollection) with optional query
   constraints. Returns { data, loading, error } where data is an array of docs
   (each with `id`).

   Usage:
     useCollection(COL.events, [where("published","==",true), orderBy("startAt")])
     useCollection([COL.pages, "home", SUB.sections], [orderBy("order")])
*/
export function useCollection(path, constraints = []) {
  const segments = Array.isArray(path) ? path : [path];
  // Stable key so the effect doesn't re-run on every render from new array identity.
  const pathKey = segments.join("/");
  const constraintKey = useConstraintKey(constraints);
  const [state, setState] = useState({ data: [], loading: true, error: null });

  useEffect(() => {
    setState((s) => ({ ...s, loading: true }));
    const ref = collection(db, segments[0], ...segments.slice(1));
    const q = constraints.length ? query(ref, ...constraints) : ref;
    const unsub = onSnapshot(
      q,
      (snap) => {
        setState({
          data: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          loading: false,
          error: null,
        });
      },
      (error) => {
        console.error(`[useCollection] ${pathKey}`, error);
        setState({ data: [], loading: false, error });
      }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey, constraintKey]);

  return state;
}

/* Derive a stable string key from query constraints so identical constraints
   built inline each render don't retrigger the subscription. */
function useConstraintKey(constraints) {
  return useMemo(
    () =>
      constraints
        .map((c) => {
          try {
            return JSON.stringify(c._field || c.type || "") + (c._op || "") + (c._value ?? "");
          } catch {
            return String(c);
          }
        })
        .join("|"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [constraints.length, JSON.stringify(constraints.map((c) => c.type ?? ""))]
  );
}
